import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AppShell from "./components/AppShell";
import HeroPage from "./pages/HeroPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import LoginPage from "./pages/LoginPage";
import AcceptInvitationPage from "./pages/AcceptInvitationPage";
import DashboardPage from "./pages/DashboardPage";
import AdminOrganizationsPage from "./pages/AdminOrganizationsPage";
import AdminOrgDetailPage from "./pages/AdminOrgDetailPage";
import AdminInviteNewPage from "./pages/AdminInviteNewPage";
import AuditLogPage from "./pages/AuditLogPage";
import CyclesListPage from "./pages/CyclesListPage";
import CycleNewPage from "./pages/CycleNewPage";
import CycleDetailPage from "./pages/CycleDetailPage";
import DomainAssessmentPage from "./pages/DomainAssessmentPage";
import { CycleDashboardIndexPage, DomainDashboardPage } from "./pages/DomainDashboardPage";
import { ExecutiveDashboardPage } from "./pages/ExecutiveDashboardPage";
import { InstructionalTeachersPage, TeacherAssessmentScoringPage } from "./pages/InstructionalTeachersPage";
import { TeacherReflectionsListPage, TeacherReflectionPage, TeacherMyScoresPage } from "./pages/TeacherPages";
import LrfLandingPage from "./pages/LrfLandingPage";
import LrfFrameworkPage from "./pages/LrfFrameworkPage";
import LrfReflectionPage from "./pages/LrfReflectionPage";
import LrfReflectionSummaryPage from "./pages/LrfReflectionSummaryPage";
import DeckAdminPage from "./pages/DeckAdminPage";
import { ForbiddenPage, NotFoundPage } from "./pages/ErrorPages";
import { Toaster } from "./components/ui/sonner";

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <Routes>
                        <Route path="/" element={<HeroPage />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route
                            path="/accept-invitation/:token"
                            element={<AcceptInvitationPage />}
                        />
                        <Route path="/forbidden" element={<ForbiddenPage />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/contact" element={<ContactPage />} />

                        <Route
                            path="/app"
                            element={
                                <ProtectedRoute>
                                    <AppShell />
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<Navigate to="dashboard" replace />} />
                            <Route path="dashboard" element={<DashboardPage />} />
                            <Route
                                path="admin/organizations"
                                element={
                                    <ProtectedRoute roles={["platform_admin"]}>
                                        <AdminOrganizationsPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="admin/organizations/:orgId"
                                element={
                                    <ProtectedRoute roles={["platform_admin", "org_leader"]}>
                                        <AdminOrgDetailPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="admin/invitations/new"
                                element={
                                    <ProtectedRoute roles={["platform_admin", "org_leader"]}>
                                        <AdminInviteNewPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="admin/audit-log"
                                element={
                                    <ProtectedRoute roles={["platform_admin", "org_leader"]}>
                                        <AuditLogPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="cycles"
                                element={
                                    <ProtectedRoute roles={["platform_admin", "org_leader", "reviewer"]}>
                                        <CyclesListPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="cycles/new"
                                element={
                                    <ProtectedRoute roles={["platform_admin", "org_leader"]}>
                                        <CycleNewPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="cycles/:cycleId"
                                element={
                                    <ProtectedRoute roles={["platform_admin", "org_leader", "reviewer", "coach"]}>
                                        <CycleDetailPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="cycles/:cycleId/domains/:domainKey"
                                element={
                                    <ProtectedRoute roles={["platform_admin", "org_leader", "reviewer", "coach"]}>
                                        <DomainAssessmentPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="cycles/:cycleId/dashboard"
                                element={
                                    <ProtectedRoute roles={["platform_admin", "org_leader", "reviewer", "coach"]}>
                                        <CycleDashboardIndexPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="cycles/:cycleId/executive"
                                element={
                                    <ProtectedRoute roles={["platform_admin", "org_leader", "reviewer", "coach"]}>
                                        <ExecutiveDashboardPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="cycles/:cycleId/dashboard/:domainKey"
                                element={
                                    <ProtectedRoute roles={["platform_admin", "org_leader", "reviewer", "coach"]}>
                                        <DomainDashboardPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="cycles/:cycleId/domains/instructional/teachers"
                                element={
                                    <ProtectedRoute roles={["platform_admin", "org_leader", "reviewer", "coach"]}>
                                        <InstructionalTeachersPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="cycles/:cycleId/domains/instructional/teachers/:teacherAssessmentId"
                                element={
                                    <ProtectedRoute roles={["platform_admin", "org_leader", "reviewer", "coach"]}>
                                        <TeacherAssessmentScoringPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="teacher/reflections"
                                element={
                                    <ProtectedRoute roles={["teacher"]}>
                                        <TeacherReflectionsListPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="teacher/reflections/:cycleId"
                                element={
                                    <ProtectedRoute roles={["teacher"]}>
                                        <TeacherReflectionPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="teacher/my-scores"
                                element={
                                    <ProtectedRoute roles={["teacher"]}>
                                        <TeacherMyScoresPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="lrf"
                                element={
                                    <ProtectedRoute roles={["platform_admin", "org_leader", "reviewer", "coach"]}>
                                        <LrfLandingPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="lrf/framework"
                                element={
                                    <ProtectedRoute roles={["platform_admin", "org_leader", "reviewer", "coach"]}>
                                        <LrfFrameworkPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="lrf/reflections/:id"
                                element={
                                    <ProtectedRoute roles={["platform_admin", "org_leader", "reviewer", "coach"]}>
                                        <LrfReflectionPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="lrf/reflections/:id/summary"
                                element={
                                    <ProtectedRoute roles={["platform_admin", "org_leader", "reviewer", "coach"]}>
                                        <LrfReflectionSummaryPage />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="admin/deck"
                                element={
                                    <ProtectedRoute roles={["platform_admin"]}>
                                        <DeckAdminPage />
                                    </ProtectedRoute>
                                }
                            />
                        </Route>

                        <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                    <Toaster position="top-right" richColors closeButton />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
